/** Character operations that combine the store with the model. UI code imports from here. */
import type { Entry } from "../../shared/snapshot-format";
import { loadEngineCatalogue } from "../data/config";
import { loadEntry } from "../data/entries";
import type { DerivedSheet } from "../engine/engine";
import type { PlayAction, SessionEvent } from "../session/actions";
import { applyPlayAction } from "../session/actions";
import { maximaFromSheet, reconcileSession, sessionDelta, sessionEngineInput, sessionUuids, tempHPOnArrival } from "../session/derive";
import type { LogEntry, ReversalPlan, RollLog } from "../session/log";
import { appendEntry, describeSessionChange, emptyRollLog, findEntry, markReversed, reversalPlan, summarizeAction } from "../session/log";
import { freshSession, type SessionMaxima } from "../session/maxima";
import { duplicateCharacter, newId, now } from "./create";
import { reconcileGrants } from "./grants";
import type { Character } from "./model";
import { deleteCharacter as removeFromStore, getCharacter, hasCharacter, listCharacters, putCharacter } from "./store";
import { parseEnvelope } from "./transfer";

export { createCharacter, duplicateCharacter, renameCharacter, ensureLevels } from "./create";
export * from "./model";
export { getCharacter, listCharacters, putCharacter, getSetting, setSetting, closeDatabase } from "./store";
export { RECENTS_CAP, listBookmarks, isBookmarked, setBookmark, listRecents, recordRecent, clearRecents, subscribePersonal } from "./personal";
export type { Bookmark, Recent as RecentEntry } from "./personal";
export type { CharacterSummary, Settings } from "./store";
export { ImportError, parseEnvelope, makeEnvelope, serializeEnvelope, exportFileName } from "./transfer";
export { shareCharacter, pickCharacterFile } from "./share";
export { ValidationError, validateCharacter } from "./validate";
export { cleanManualIWR, IWR_KINDS, IWR_TABLE_BY_KIND } from "./iwr";
export { MigrationError, migrateCharacter } from "./migrate";
export { learnSpell, forgetSpell, setSignature, prepareSpell, addManualEntry, updateManualEntry, removeManualEntry, spellCandidates, spellRecordFacts } from "./spells";
export { addItem, addKit, removeItem, setQuantity, setCarry, setInSlot, setInvested, moveToContainer, setRunes, setMaterial, setTraitToggles, setAmmunition, setItemHp, setCoins, addCoins, removeCoins, itemCandidates, itemRecordFacts, defaultCarry } from "./inventory";
export type { InventoryChangeResult, ItemCandidateQuery, ItemRecordFacts, CarryChange } from "./inventory";
export type { SpellChangeResult, SpellCandidateQuery, SpellRecordFacts, EntryView } from "./spells";
export { reconcileGrants, reconcileOnce, clearSelection, setSelection, setAnswer, MAX_PASSES } from "./grants";
export type { ReconcileResult, ChangeResult } from "./grants";

export interface MaterialisedCharacter {
  character: Character;
  /** Every Entry the Character's Selections and Session name (granted ones included), for deriving the sheet. */
  entries: Map<string, Entry>;
  /** The sheet with the Session's Conditions and Effects applied, when a Session is open. */
  sheet: DerivedSheet;
  /** Whether the Character changed and should be saved. */
  changed: boolean;
  /** What the open Session is measured against; null without a Session. */
  maxima: SessionMaxima | null;
}

/** Every Selection UUID a Character names, and every Entry its inventory holds (#11). */
export function selectionUuids(character: Character): string[] {
  return [...new Set([...character.levels.flatMap((l) => l.choices.flatMap((c) => (c.selection ? [c.selection.uuid] : []))), ...character.inventory.items.map((i) => i.uuid)])];
}

/**
 * Load the Entries a Character (and its Session) names, then reconcile its grants (issue #6)
 * and its Session's grants (#18) until both settle, loading whatever else the engine asks for.
 * The result's Character holds every granted Selection and Session item; save it when `changed`.
 */
export async function materialiseGrants(input: Character, entries: Map<string, Entry> = new Map()): Promise<MaterialisedCharacter> {
  const catalogue = await loadEngineCatalogue();
  const load = async (uuids: Iterable<string>): Promise<void> => {
    const missing = [...new Set(uuids)].filter((u) => !entries.has(u));
    const loaded = await Promise.all(missing.map(async (u) => [u, await loadEntry(u)] as const));
    for (const [u, e] of loaded) if (e) entries.set(u, e);
  };
  await load([...selectionUuids(input), ...sessionUuids(input.session)]);
  let character = input;
  let changed = false;
  const finish = (sheet: DerivedSheet): MaterialisedCharacter => ({ character, entries, sheet, changed, maxima: character.session ? maximaFromSheet(character, sheet, entries, catalogue) : null });
  const pass = (): { sheet: DerivedSheet; changed: boolean; needed: string[] } => {
    const result = reconcileGrants({ character, entries, catalogue, ...sessionEngineInput(character.session, entries) });
    character = result.character;
    let touched = result.changed;
    if (character.session) {
      const r = reconcileSession(character.session, result.sheet);
      if (r.changed) {
        character = { ...character, session: r.session };
        touched = true;
      }
    }
    changed ||= touched;
    return { sheet: result.sheet, changed: touched, needed: result.needed };
  };
  for (let round = 0; round < 12; round++) {
    const result = pass();
    const wanted = [...selectionUuids(character), ...sessionUuids(character.session), ...result.needed].filter((u) => !entries.has(u));
    if (wanted.length === 0 && !result.changed) return finish(result.sheet);
    const before = entries.size;
    await load(wanted);
    // Nothing more can be loaded: stop with what there is (the sheet reports the missing Entries).
    if (entries.size === before && !result.changed) return finish(result.sheet);
  }
  return finish(pass().sheet);
}

export interface PlayedAction {
  character: Character;
  sheet: DerivedSheet;
  maxima: SessionMaxima;
  entries: Map<string, Entry>;
  events: SessionEvent[];
  /** The inverse Play Action, as the Roll Log entry holds it; changes nothing when `blocked`. */
  inverse: PlayAction;
  /** The Roll Log entry written for the action; null when `blocked`. */
  entry: LogEntry | null;
  blocked: string | null;
}

interface PlayOptions {
  /** ISO timestamp for the Roll Log entry. */
  at?: string;
  /** The entry this action reverses: the new entry links to it and marks it reversed. */
  reverses?: string;
}

/**
 * Apply a Play Action to the Character's Session, then settle grants and the sheet. A newly
 * applied Effect or Condition that grants temporary HP on arrival sets them here, as Foundry's
 * TempHP `onCreate` does, and that step joins the inverse. The action, its events and its
 * inverse are written to the Roll Log (#20); start fresh clears the log first, so its own entry
 * opens the new one.
 */
export async function playSessionAction(input: Character, action: PlayAction, entries: Map<string, Entry> = new Map(), options: PlayOptions = {}): Promise<PlayedAction> {
  const before = await materialiseGrants(input, entries);
  if (!before.character.session || !before.maxima) throw new Error("no Session is open");
  const applied = applyPlayAction(before.character.session, before.maxima, action);
  if (applied.blocked) return { character: before.character, sheet: before.sheet, maxima: before.maxima, entries, events: applied.events, inverse: applied.inverse, entry: null, blocked: applied.blocked };
  let after = await materialiseGrants({ ...before.character, session: applied.session }, entries);
  // What the reconciler added or removed (grants) is undone first, then the action itself.
  const inverse: PlayAction[] = [...sessionDelta(applied.session, after.character.session ?? applied.session), applied.inverse];
  const events = [...applied.events];
  const arrived = action.kind === "apply-effect" ? action.uuid : action.kind === "apply-condition" ? after.character.session?.conditions.find((c) => c.slug === action.slug)?.uuid : undefined;
  if (arrived && after.character.session && after.maxima) {
    const temp = tempHPOnArrival(after.sheet, arrived);
    if (temp !== null && temp > after.character.session.hp.temp) {
      const step = applyPlayAction(after.character.session, after.maxima, { kind: "set-temp-hp", value: temp, source: after.entries.get(arrived)?.name ?? arrived });
      if (!step.blocked) {
        events.push(...step.events);
        const settled = await materialiseGrants({ ...after.character, session: step.session }, entries);
        inverse.unshift(...sessionDelta(step.session, settled.character.session ?? step.session), step.inverse);
        after = settled;
      }
    }
  }
  const combined: PlayAction = inverse.length === 1 ? inverse[0]! : { kind: "batch", label: `undo ${action.kind}`, steps: inverse };
  const logged = logAction(before.character, after.character, action, combined, events, options);
  return {
    character: { ...after.character, rollLog: logged.log },
    sheet: after.sheet,
    maxima: after.maxima!,
    entries,
    events: logged.entry.events,
    inverse: combined,
    entry: logged.entry,
    blocked: null,
  };
}

/** Write the Roll Log entry for an applied action. A reversal names what it reversed and describes the change, as its primitives raise no events. */
function logAction(before: Character, after: Character, action: PlayAction, inverse: PlayAction, events: SessionEvent[], options: PlayOptions): { log: RollLog; entry: LogEntry } {
  const reversed = options.reverses ? findEntry(before.rollLog, options.reverses) : undefined;
  let log = action.kind === "start-fresh" ? emptyRollLog() : before.rollLog;
  const described = reversed && before.session && after.session ? describeSessionChange(before.session, after.session) : events;
  const summary = reversed ? `Reversed: ${reversed.summary}` : action.kind === "start-fresh" ? "Session started fresh (the earlier Roll Log was cleared)" : summarizeAction(action, events);
  const type = reversed ? reversed.type : action.kind === "roll" ? "roll" : "session";
  const appended = appendEntry(log, {
    at: options.at ?? now(),
    round: after.session?.round ?? null,
    kind: reversed ? "reversal" : action.kind,
    type,
    summary,
    events: described,
    action,
    inverse: reversed ? null : inverse,
    reverses: reversed?.id ?? null,
  });
  log = appended.log;
  if (reversed) log = markReversed(log, reversed.id, appended.entry.id);
  return { log, entry: appended.entry };
}

export interface ReversedActions {
  character: Character;
  sheet: DerivedSheet;
  maxima: SessionMaxima;
  entries: Map<string, Entry>;
  /** The reversal entries written, in the order applied (dependents newest first, then the entry). */
  reversed: LogEntry[];
  /** Why the reversal stopped short; what was reversed before it stays reversed. */
  blocked: string | null;
}

/** What reversing a Roll Log entry takes: the entry and the later entries that depend on it. Null when it cannot be reversed. */
export function planReversal(character: Character, id: string): ReversalPlan | null {
  return reversalPlan(character.rollLog, id);
}

/**
 * Reverse a Roll Log entry (#20): its dependents newest first, then the entry, each inverse
 * applied as a Play Action of its own and logged linked to what it reversed. Exact by
 * construction: every inverse meets the Session it was made from.
 */
export async function reverseLogEntry(input: Character, id: string, entries: Map<string, Entry> = new Map(), at?: string): Promise<ReversedActions> {
  const plan = planReversal(input, id);
  const before = await materialiseGrants(input, entries);
  if (!before.character.session || !before.maxima) throw new Error("no Session is open");
  let current: PlayedAction = { character: before.character, sheet: before.sheet, maxima: before.maxima, entries, events: [], inverse: { kind: "batch", label: "nothing", steps: [] }, entry: null, blocked: null };
  const reversed: LogEntry[] = [];
  if (!plan) return { ...current, reversed, blocked: "that entry cannot be reversed" };
  for (const entry of plan.order) {
    const played = await playSessionAction(current.character, entry.inverse!, entries, { ...(at ? { at } : {}), reverses: entry.id });
    if (played.blocked) return { character: current.character, sheet: current.sheet, maxima: current.maxima, entries, reversed, blocked: `${entry.summary}: ${played.blocked}` };
    current = played;
    reversed.push(played.entry!);
  }
  return { character: current.character, sheet: current.sheet, maxima: current.maxima, entries, reversed, blocked: null };
}

/** Open a Session at the Character's derived maxima (start fresh). */
export async function startSession(input: Character, at = now()): Promise<MaterialisedCharacter> {
  const base = await materialiseGrants({ ...input, session: null, rollLog: emptyRollLog() });
  const catalogue = await loadEngineCatalogue();
  const maxima = maximaFromSheet(base.character, base.sheet, base.entries, catalogue);
  const opened = await materialiseGrants({ ...base.character, session: freshSession(maxima, at) }, base.entries);
  return { ...opened, changed: true };
}

/** Close the Session: the play state and its Roll Log are discarded and the Character is no longer in play. */
export function closeSession(character: Character): Character {
  return { ...character, session: null, rollLog: emptyRollLog() };
}

/**
 * Save an imported Character. When a Character with its id already exists, the import becomes a
 * copy with a new id; nothing is ever overwritten.
 */
export async function saveImported(input: Character): Promise<{ character: Character; copied: boolean }> {
  let character = input;
  let copied = false;
  if (await hasCharacter(character.id)) {
    character = { ...character, id: newId() };
    copied = true;
  }
  return { character: await putCharacter(character), copied };
}

/** Import a Character from file text as it is. The app imports through src/migration, which migrates a file from an older Snapshot first (#17). */
export async function importCharacter(text: string): Promise<{ character: Character; copied: boolean }> {
  return saveImported(parseEnvelope(text).character);
}

/** Save a copy of a stored Character under a new name. */
export async function duplicateStoredCharacter(id: string, name?: string): Promise<Character> {
  const source = await getCharacter(id);
  if (!source) throw new Error(`no Character ${id}`);
  return putCharacter(duplicateCharacter(source, name));
}

export async function deleteCharacter(id: string): Promise<void> {
  await removeFromStore(id);
}
